import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AppFeature,
  AdminPermission,
  AdminVenueBreadcrumbDto,
  AdminStickerDto,
  AdminVenueEventCommentListItemDto,
  AdminEventRsvpDto,
  AdminVisitPlanListItemDto,
  AuditLogDto,
  AuditLogEventType,
  EventDateRangeDto,
  EventbriteEventImportDto,
  EventbriteOrganizationEventDto,
  VenueDetailDto,
  VenueEventExternalLinkDto,
  VenueEventVisibility,
  VenueSpecialDto,
  VenueType,
  VisitDto,
  VenuePeekDto,
  ScheduledBlastListItemDto,
  ScheduledBlastStatus,
  parseEventbriteEventId,
} from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import Table from '../../components/Table'
import { googleMapsApiKey } from '../../config'
import LocationView, { LocationMap } from '../../components/locations/LocationView'
import Modal from 'react-modal'
import Avatar from '../../components/Avatar'
import FormItem from '../../components/FormItem'
import TextInput from '../../components/ui/TextInput'
import ToggleInput from '../../components/ui/ToggleInput'
import AdminEventDateRangesField from '../../components/admin/AdminEventDateRangesField'
import AdminDateRangeCalendarRow from '../../components/admin/AdminDateRangeCalendarRow'
import VisitMap from '../../components/VisitMap'
import { formatCoordinates } from '@ivy-monorepo/shared'
import BlastModal from '../../components/modals/BlastModal'
import EventRsvpBlastModal from '../../components/modals/EventRsvpBlastModal'
import Icon from 'src/components/Icon'
import VenueLogoModal from 'src/components/modals/VenueLogoModal'
import EditVenueLocationModal from 'src/components/modals/EditVenueLocationModal'
import VenueOpeningHoursEditor from '../../components/admin/VenueOpeningHoursEditor'
import ConfirmationModal from 'src/components/modals/ConfirmationModal'
import VenueBreadcrumbModal from 'src/components/modals/VenueBreadcrumbModal'
import StickerModal from 'src/components/modals/StickerModal'
import VenueEventCommentModal from 'src/components/modals/VenueEventCommentModal'
import ImageDropzone from '../../components/ui/ImageDropzone'
import Pagination from '../../components/Pagination'
import { usePermissions } from '../../contexts/PermissionsContext'
import dayjs from 'dayjs'
import { formatEventbriteStart } from '../../utils/datetimeInput'
import { withCalendarStatusLabel } from '../../utils/googleCalendarStatus'
import { centsToDollarInput, dollarInputToCents } from '../../utils/ticketPrice'

type TabKey = 'details' | 'visits' | 'map' | 'specials' | 'peeks' | 'socials' | 'blasts' | 'breadcrumbs' | 'stickers' | 'event-comments' | 'vibes' | 'intentions' | 'visit-plans' | 'event-rsvps' | 'back'

const VENUE_EVENT_VISIBILITY_LABELS: Record<VenueEventVisibility, string> = {
  [VenueEventVisibility.Public]: 'Public (everyone)',
  [VenueEventVisibility.InternalOnly]: 'Internal only',
}

const USER_INTENTION_AUDIT_EVENT_TYPES = `${AuditLogEventType.SetUserIntention},${AuditLogEventType.ClearUserIntention}`

const VENUE_DETAIL_TABS: Array<{ key: TabKey; label: string; permission: AdminPermission }> = [
  { key: 'details', label: 'Details', permission: AdminPermission.Venues },
  { key: 'socials', label: 'Socials', permission: AdminPermission.Venues },
  { key: 'visits', label: 'Visits', permission: AdminPermission.Visits },
  { key: 'breadcrumbs', label: 'Breadcrumbs', permission: AdminPermission.Breadcrumbs },
  { key: 'stickers', label: 'Stickers', permission: AdminPermission.Stickers },
  { key: 'event-comments', label: 'Event comments', permission: AdminPermission.Events },
  { key: 'visit-plans', label: 'Visit plans', permission: AdminPermission.Events },
  { key: 'event-rsvps', label: 'RSVPs', permission: AdminPermission.Events },
  { key: 'peeks', label: 'Peeks', permission: AdminPermission.Venues },
  { key: 'specials', label: 'Specials', permission: AdminPermission.Venues },
  { key: 'map', label: 'Map', permission: AdminPermission.Venues },
  { key: 'blasts', label: 'Scheduled Blasts', permission: AdminPermission.Venues },
  { key: 'vibes', label: 'Vibe', permission: AdminPermission.AuditLogs },
  { key: 'intentions', label: 'Intentions', permission: AdminPermission.Intentions },
]

/** Eventbrite listing URL as the event's `ivy-event` tracking link (`?aff=ivy-event`). */
const withIvyEventTrackingCode = (url: string): string => {
  try {
    const parsed = new URL(url)
    parsed.searchParams.set('aff', 'ivy-event')
    return parsed.toString()
  } catch {
    return url
  }
}

/** "Tickets $15.00–$25.00 on Eventbrite" / "Free on Eventbrite" for the import summary. */
const formatEventbritePrice = (event: EventbriteEventImportDto): string => {
  if (event.isFree) return 'Free on Eventbrite.'
  if (event.minPriceCents === null) return 'No ticket price on Eventbrite.'
  const format = (cents: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: event.currency || 'USD' }).format(cents / 100)
  const range =
    event.maxPriceCents !== null && event.maxPriceCents !== event.minPriceCents
      ? `${format(event.minPriceCents)}–${format(event.maxPriceCents)}`
      : format(event.minPriceCents)
  return `Tickets ${range} on Eventbrite (Ivy tickets stay off unless you turn on Sell tickets).`
}

const METERS_PER_MILE = 1609.34

// Radius is stored in meters; the table shows miles.
const toBlastRow = (blast: ScheduledBlastListItemDto) => ({
  ...blast,
  radiusMiles: Math.round((blast.radius / METERS_PER_MILE) * 10) / 10,
  scheduledDateCentral: dayjs.utc(blast.scheduledDate).tz('America/Chicago').format('YYYY-MM-DD HH:mm'),
})

const VenueDetailPage = () => {
  const { publicId } = useParams<{ publicId: string }>()
  const navigate = useNavigate()
  const { hasPermission, isLoading: isPermissionsLoading, features } = usePermissions()
  const [venueDetail, setVenueDetail] = useState<VenueDetailDto | null>(null)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [visits, setVisits] = useState<VisitDto[]>([])
  const [blasts, setBlasts] = useState<ScheduledBlastListItemDto[]>([])
  const [specials, setSpecials] = useState<VenueSpecialDto[]>([])
  const [peeks, setPeeks] = useState<VenuePeekDto[]>([])
  const [selectedCoordinates, setSelectedCoordinates] = useState<{ latitude: number; longitude: number } | null>(null)
  const [editSpecial, setEditSpecial] = useState<VenueSpecialDto | null>(null)
  const [ticketPriceInput, setTicketPriceInput] = useState('')
  const [pendingFlyerFile, setPendingFlyerFile] = useState<File | null>(null)
  const [pendingFlyerPreviewUrl, setPendingFlyerPreviewUrl] = useState<string | null>(null)
  const [isPropertyBarVisible, setIsPropertyBarVisible] = useState(false)
  const [isSavingSpecial, setIsSavingSpecial] = useState(false)
  const [specialFormError, setSpecialFormError] = useState<string | null>(null)
  const [eventbriteLink, setEventbriteLink] = useState('')
  const [isImportingEventbrite, setIsImportingEventbrite] = useState(false)
  const [eventbriteImport, setEventbriteImport] = useState<EventbriteEventImportDto | null>(null)
  /** Upcoming events of Ivy's Eventbrite organization; null until loaded. */
  const [eventbriteEvents, setEventbriteEvents] = useState<EventbriteOrganizationEventDto[] | null>(null)
  const [eventbriteEventsError, setEventbriteEventsError] = useState<string | null>(null)
  const [selectedVisit, setSelectedVisit] = useState<string | null>(null)
  const [selectedScheduledBlast, setSelectedScheduledBlast] = useState<string | null>(null)
  const [editingBlast, setEditingBlast] = useState<ScheduledBlastListItemDto | null>(null)
  const [isBlastDeleting, setIsBlastDeleting] = useState<boolean>(false)
  const [deleteSpecialTarget, setDeleteSpecialTarget] = useState<VenueSpecialDto | null>(null)
  const [isSpecialDeleting, setIsSpecialDeleting] = useState<boolean>(false)
  const [visitDetail, setVisitDetail] = useState<VisitDto | null>(null)
  const [isBlastModalOpen, setIsBlastModalOpen] = useState(false)
  const [isLogoEditModalOpen, setIsLogoEditModalOpen] = useState(false)
  const [isLocationEditModalOpen, setIsLocationEditModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<TabKey>('details')
  const [isMapVisibilityPending, setIsMapVisibilityPending] = useState(false)
  const [isVenueTypePending, setIsVenueTypePending] = useState(false)
  const [isSpotlightPending, setIsSpotlightPending] = useState(false)
  const [editedVenueName, setEditedVenueName] = useState('')
  const [isVenueNamePending, setIsVenueNamePending] = useState(false)
  const [venueNameError, setVenueNameError] = useState<string | null>(null)
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false)
  const [closeConfirmName, setCloseConfirmName] = useState('')
  const [isClosePending, setIsClosePending] = useState(false)
  const [isReopenPending, setIsReopenPending] = useState(false)
  const [breadcrumbs, setBreadcrumbs] = useState<AdminVenueBreadcrumbDto[]>([])
  const [breadcrumbsPagination, setBreadcrumbsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [breadcrumbsPage, setBreadcrumbsPage] = useState(1)
  const [breadcrumbsSortBy, setBreadcrumbsSortBy] = useState('createdAt')
  const [breadcrumbsSortOrder, setBreadcrumbsSortOrder] = useState('desc')
  const [selectedBreadcrumbPublicId, setSelectedBreadcrumbPublicId] = useState<string | null>(null)
  const [stickers, setStickers] = useState<AdminStickerDto[]>([])
  const [stickersPagination, setStickersPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [stickersPage, setStickersPage] = useState(1)
  const [stickersSortBy, setStickersSortBy] = useState('createdAt')
  const [stickersSortOrder, setStickersSortOrder] = useState('desc')
  const [selectedStickerPublicId, setSelectedStickerPublicId] = useState<string | null>(null)
  const [eventComments, setEventComments] = useState<AdminVenueEventCommentListItemDto[]>([])
  const [eventCommentsPagination, setEventCommentsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [eventCommentsPage, setEventCommentsPage] = useState(1)
  const [eventCommentsSortBy, setEventCommentsSortBy] = useState('createdAt')
  const [eventCommentsSortOrder, setEventCommentsSortOrder] = useState('desc')
  const [selectedCommentPublicId, setSelectedCommentPublicId] = useState<string | null>(null)
  const [vibeLogs, setVibeLogs] = useState<AuditLogDto[]>([])
  const [vibeLogsPagination, setVibeLogsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [vibeLogsPage, setVibeLogsPage] = useState(1)
  const [intentionLogs, setIntentionLogs] = useState<AuditLogDto[]>([])
  const [intentionLogsPagination, setIntentionLogsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [intentionLogsPage, setIntentionLogsPage] = useState(1)
  const [eventRsvps, setEventRsvps] = useState<AdminEventRsvpDto[]>([])
  const [eventRsvpsPagination, setEventRsvpsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [eventRsvpsPage, setEventRsvpsPage] = useState(1)
  // RSVPs tab: optional event filter, and the "Send blast" (push to RSVPs) modal.
  const [eventRsvpsEventFilter, setEventRsvpsEventFilter] = useState('')
  const [isRsvpBlastModalOpen, setIsRsvpBlastModalOpen] = useState(false)
  const [visitPlans, setVisitPlans] = useState<AdminVisitPlanListItemDto[]>([])
  const [visitPlansPagination, setVisitPlansPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [visitPlansPage, setVisitPlansPage] = useState(1)

  useEffect(() => {
    const fetchVenueDetail = async () => {
      if (!publicId) {
        setError('Venue not found')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        const response = await adminService.getVenueDetail(publicId)
        setVenueDetail(response)
        setEditedVenueName(response.name ?? '')
        setVenueNameError(null)
      } catch (err) {
        console.log('Failed to fetch venue detail', err)
        setError('Failed to load venue details.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchVenueDetail()
  }, [publicId])

  useEffect(() => {
    if (!venueDetail?.photoReference) {
      setPhotoUrl(null)
      return
    }

    setPhotoUrl(`https://maps.googleapis.com/maps/api/place/photo?maxwidth=800&photo_reference=${venueDetail.photoReference}&key=${googleMapsApiKey}`)
  }, [venueDetail?.photoReference])

  useEffect(() => {
    if (activeTab !== 'visits' || !publicId) {
      return
    }

    const fetchVisits = async () => {
      try {
        const data = await adminService.getVenueVisits(publicId)
        setVisits(data)
      } catch (err) {
        console.log('Failed to fetch venue visits', err)
      }
    }

    fetchVisits()
  }, [activeTab, publicId])

  useEffect(() => {
    if (activeTab !== 'blasts' || !publicId) {
      return
    }

    const fetchVenueBlasts = async () => {
      try {
        const data = await adminService.getVenueBlasts(publicId)
        setBlasts(data.map(toBlastRow))
      } catch (err) {
        console.log('Failed to fetch venue blasts', err)
      }
    }

    fetchVenueBlasts()
  }, [activeTab, publicId])

  useEffect(() => {
    if ((activeTab !== 'specials' && activeTab !== 'event-rsvps') || !publicId) {
      return
    }

    const fetchSpecials = async () => {
      try {
        const data = await adminService.getVenueSpecials(publicId)
        setSpecials(data)
      } catch (err) {
        console.log('Failed to fetch venue specials', err)
      }
    }

    fetchSpecials()
  }, [activeTab, publicId])

  useEffect(() => {
    if (activeTab !== 'peeks' || !publicId) {
      return
    }

    const fetchPeeks = async () => {
      try {
        const data = await adminService.getVenuePeeks(publicId)
        // Add synthetic index key to each peek for unique row identification
        const peeksWithIndex = data.map((peek, index) => ({ ...peek, _index: index }))
        setPeeks(peeksWithIndex)
      } catch (err) {
        console.log('Failed to fetch venue peeks', err)
      }
    }

    fetchPeeks()
  }, [activeTab, publicId])

  const fetchVenueBreadcrumbs = useCallback(async () => {
    if (!publicId) {
      return
    }

    try {
      const response = await adminService.getVenueBreadcrumbs(
        breadcrumbsPage,
        10,
        '',
        breadcrumbsSortBy,
        breadcrumbsSortOrder,
        { venuePublicId: publicId },
      )
      setBreadcrumbs(response.breadcrumbs)
      setBreadcrumbsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch venue breadcrumbs', err)
    }
  }, [breadcrumbsPage, breadcrumbsSortBy, breadcrumbsSortOrder, publicId])

  useEffect(() => {
    if (activeTab !== 'breadcrumbs' || !publicId || !hasPermission(AdminPermission.Breadcrumbs)) {
      return
    }

    fetchVenueBreadcrumbs()
  }, [activeTab, fetchVenueBreadcrumbs, hasPermission, publicId])

  const fetchVenueStickers = useCallback(async () => {
    if (!publicId) {
      return
    }

    try {
      const response = await adminService.getStickers(
        stickersPage,
        10,
        '',
        stickersSortBy,
        stickersSortOrder,
        { venuePublicId: publicId },
      )
      setStickers(response.stickers)
      setStickersPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch venue stickers', err)
    }
  }, [stickersPage, stickersSortBy, stickersSortOrder, publicId])

  useEffect(() => {
    if (activeTab !== 'stickers' || !publicId || !hasPermission(AdminPermission.Stickers)) {
      return
    }

    fetchVenueStickers()
  }, [activeTab, fetchVenueStickers, hasPermission, publicId])

  const fetchVenueEventComments = useCallback(async () => {
    if (!publicId) {
      return
    }

    try {
      const response = await adminService.getEventComments(
        eventCommentsPage,
        10,
        '',
        eventCommentsSortBy,
        eventCommentsSortOrder as 'asc' | 'desc',
        publicId,
      )
      setEventComments(response.comments)
      setEventCommentsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch venue event comments', err)
    }
  }, [eventCommentsPage, eventCommentsSortBy, eventCommentsSortOrder, publicId])

  useEffect(() => {
    if (activeTab !== 'event-comments' || !publicId || !hasPermission(AdminPermission.Events)) {
      return
    }

    fetchVenueEventComments()
  }, [activeTab, fetchVenueEventComments, hasPermission, publicId])

  const fetchVenueVibes = useCallback(async () => {
    if (!publicId) {
      return
    }

    try {
      const response = await adminService.getAuditLogs(
        vibeLogsPage,
        10,
        '',
        'desc',
        AuditLogEventType.SetVibeEmoji,
        undefined,
        publicId,
      )

      const normalized = response.auditLogs.map((log) => {
        const anyLog = log as any
        const data = anyLog?.data
        if (typeof data === 'string') {
          try {
            return { ...anyLog, data: JSON.parse(data) }
          } catch {
            return anyLog
          }
        }
        return anyLog
      }) as AuditLogDto[]

      setVibeLogs(normalized)
      setVibeLogsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch venue vibe logs', err)
    }
  }, [publicId, vibeLogsPage])

  useEffect(() => {
    if (activeTab !== 'vibes' || !publicId || !hasPermission(AdminPermission.AuditLogs)) {
      return
    }

    fetchVenueVibes()
  }, [activeTab, fetchVenueVibes, hasPermission, publicId])

  const fetchVenueIntentions = useCallback(async () => {
    if (!publicId) {
      return
    }

    try {
      const response = await adminService.getAuditLogs(
        intentionLogsPage,
        10,
        '',
        'desc',
        USER_INTENTION_AUDIT_EVENT_TYPES,
        undefined,
        publicId,
      )

      const normalized = response.auditLogs.map((log) => {
        const anyLog = log as any
        const data = anyLog?.data
        if (typeof data === 'string') {
          try {
            return { ...anyLog, data: JSON.parse(data) }
          } catch {
            return anyLog
          }
        }
        return anyLog
      }) as AuditLogDto[]

      setIntentionLogs(normalized)
      setIntentionLogsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch venue intention logs', err)
    }
  }, [intentionLogsPage, publicId])

  useEffect(() => {
    if (activeTab !== 'intentions' || !publicId || !hasPermission(AdminPermission.Intentions)) {
      return
    }

    fetchVenueIntentions()
  }, [activeTab, fetchVenueIntentions, hasPermission, publicId])

  const fetchVenueEventRsvps = useCallback(async () => {
    if (!publicId) {
      return
    }

    try {
      const response = await adminService.getEventRsvps(
        eventRsvpsPage,
        10,
        '',
        'rsvpedAt',
        'desc',
        publicId,
        { eventPublicId: eventRsvpsEventFilter || undefined },
      )
      setEventRsvps(response.rsvps)
      setEventRsvpsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch venue event RSVPs', err)
    }
  }, [eventRsvpsPage, eventRsvpsEventFilter, publicId])

  const fetchVenueVisitPlans = useCallback(async () => {
    if (!publicId) {
      return
    }

    try {
      const response = await adminService.getVisitPlans(
        visitPlansPage,
        10,
        '',
        'plannedDate',
        'desc',
        publicId,
      )
      setVisitPlans(response.visitPlans.map(withCalendarStatusLabel))
      setVisitPlansPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch venue visit plans', err)
    }
  }, [publicId, visitPlansPage])

  const handleSyncVisitPlanToGoogleCalendar = useCallback(
    async (row: AdminVisitPlanListItemDto) => {
      if (!row.googleCalendarConnected) return
      await adminService.syncVisitPlanToGoogleCalendar(row.publicId)
      fetchVenueVisitPlans()
    },
    [fetchVenueVisitPlans],
  )

  useEffect(() => {
    if (activeTab !== 'event-rsvps' || !publicId || !hasPermission(AdminPermission.Events)) {
      return
    }

    fetchVenueEventRsvps()
  }, [activeTab, fetchVenueEventRsvps, hasPermission, publicId])

  useEffect(() => {
    if (activeTab !== 'visit-plans' || !publicId || !hasPermission(AdminPermission.Events)) {
      return
    }

    fetchVenueVisitPlans()
  }, [activeTab, fetchVenueVisitPlans, hasPermission, publicId])

  // Fetch visit detail when selectedVisit changes
  useEffect(() => {
    if (!selectedVisit) {
      setVisitDetail(null)
      return
    }

    const fetchVisitDetail = async () => {
      try {
        const detail = await adminService.getVisitDetail(selectedVisit)
        setVisitDetail(detail)
      } catch (err) {
        console.log('Failed to fetch visit detail', err)
        setVisitDetail(null)
      }
    }

    fetchVisitDetail()
  }, [selectedVisit])

  const handleTabChange = (tabKey: TabKey) => {
    if (tabKey === 'back') {
      navigate(-1)
      return
    }
    setActiveTab(tabKey as TabKey)
  }

  const handleCoordinatesClick = (latitude: number, longitude: number) => {
    setSelectedCoordinates({ latitude, longitude })
  }

  const resetEventbriteImport = () => {
    setEventbriteLink('')
    setIsImportingEventbrite(false)
    setEventbriteImport(null)
  }

  const isEventFormOpen = isPropertyBarVisible && !!editSpecial
  // Loaded once per page, the first time the event form opens with the import on.
  useEffect(() => {
    if (!isEventFormOpen || !features[AppFeature.Eventbrite] || eventbriteEvents !== null) return
    let cancelled = false
    adminService
      .listEventbriteEvents()
      .then(events => {
        if (!cancelled) setEventbriteEvents(events)
      })
      .catch((err: any) => {
        if (cancelled) return
        setEventbriteEvents([])
        setEventbriteEventsError(err?.friendlyMessage || err?.message || 'Could not load Eventbrite events.')
      })
    return () => {
      cancelled = true
    }
  }, [isEventFormOpen, features, eventbriteEvents])

  /** Prefills the open event form from an Eventbrite event; the admin reviews and saves as usual. */
  const handleImportEventbrite = async (linkOverride?: string) => {
    const link = (linkOverride ?? eventbriteLink).trim()
    if (!link || isImportingEventbrite) return
    setIsImportingEventbrite(true)
    setSpecialFormError(null)
    try {
      const imported = await adminService.importEventbriteEvent(link)
      setEventbriteImport(imported)
      setEditSpecial(prev => {
        if (!prev) return null
        return {
          ...prev,
          name: imported.name || prev.name,
          content: imported.content || prev.content,
          eventDateRanges: imported.eventStartDate
            ? [{ eventStartDate: imported.eventStartDate, eventEndDate: imported.eventEndDate }]
            : prev.eventDateRanges,
          externalLink: { name: 'Get tickets on Eventbrite', link: withIvyEventTrackingCode(imported.url) },
        }
      })
      if (imported.imageDataUrl) {
        const blob = await (await fetch(imported.imageDataUrl)).blob()
        const extension = blob.type.split('/')[1] || 'jpg'
        const file = new File([blob], `eventbrite-${imported.eventbriteId}.${extension}`, { type: blob.type })
        if (pendingFlyerPreviewUrl) {
          URL.revokeObjectURL(pendingFlyerPreviewUrl)
        }
        setPendingFlyerFile(file)
        setPendingFlyerPreviewUrl(URL.createObjectURL(file))
      }
    } catch (err: any) {
      setSpecialFormError(err?.friendlyMessage || err?.message || 'Could not import the Eventbrite event.')
    } finally {
      setIsImportingEventbrite(false)
    }
  }

  const handleSpecialRowClick = (special: VenueSpecialDto) => {
    if (pendingFlyerPreviewUrl) {
      URL.revokeObjectURL(pendingFlyerPreviewUrl)
    }
    setPendingFlyerFile(null)
    setPendingFlyerPreviewUrl(null)
    setSpecialFormError(null)
    resetEventbriteImport()
    setTicketPriceInput(centsToDollarInput(special.ticketPriceCents))
    setEditSpecial({
      ...special,
      externalLink: special.externalLink ? { ...special.externalLink } : null,
      eventDateRanges: [{ eventStartDate: special.eventStartDate ?? null, eventEndDate: special.eventEndDate ?? null }],
    })
    setIsPropertyBarVisible(true)
  }

  const handleAddSpecial = () => {
    if (pendingFlyerPreviewUrl) {
      URL.revokeObjectURL(pendingFlyerPreviewUrl)
    }
    setPendingFlyerFile(null)
    setPendingFlyerPreviewUrl(null)
    setSpecialFormError(null)
    resetEventbriteImport()
    setTicketPriceInput('')
    setEditSpecial({
      publicId: '',
      name: '',
      content: '',
      dayOfWeek: '',
      startDate: '',
      endDate: '',
      externalLink: null,
      hideAddress: null,
      eventAddress: null,
      visibility: VenueEventVisibility.Public,
      isTicketed: false,
      ticketPriceCents: null,
      eventDateRanges: [{ eventStartDate: null, eventEndDate: null }],
    } as unknown as VenueSpecialDto)
    setIsPropertyBarVisible(true)
  }

  const handleCancelEditSpecial = () => {
    if (pendingFlyerPreviewUrl) {
      URL.revokeObjectURL(pendingFlyerPreviewUrl)
    }
    setPendingFlyerFile(null)
    setPendingFlyerPreviewUrl(null)
    setSpecialFormError(null)
    resetEventbriteImport()
    setIsPropertyBarVisible(false)
    setEditSpecial(null)
  }

  const handleSaveSpecial = async () => {
    if (!publicId || !editSpecial) return

    window.dispatchEvent(new CustomEvent('validateInputs'))

    if (!editSpecial.name?.trim() || !editSpecial.content?.trim()) {
      setSpecialFormError('Name and content are required.')
      return
    }

    const eventDateRanges = (editSpecial.eventDateRanges || []).filter(range => range.eventStartDate)
    if (eventDateRanges.length === 0) {
      setSpecialFormError('Add at least one event date before saving.')
      return
    }

    const ticketPriceCents = dollarInputToCents(ticketPriceInput)
    if (editSpecial.isTicketed && ticketPriceCents === null) {
      setSpecialFormError('Enter a ticket price greater than $0 (e.g. 12.50).')
      return
    }

    setSpecialFormError(null)
    setIsSavingSpecial(true)

    try {
      const name = editSpecial.externalLink?.name?.trim() ?? ''
      const link = editSpecial.externalLink?.link?.trim() ?? ''
      const externalLink = name && link ? { name, link } : null

      const payload = {
        name: editSpecial.name,
        content: editSpecial.content,
        dayOfWeek: editSpecial.dayOfWeek !== null && editSpecial.dayOfWeek !== '' ? editSpecial.dayOfWeek : null,
        startDate: editSpecial.startDate || null,
        endDate: editSpecial.endDate || null,
        eventDateRanges,
        externalLink,
        hideAddress: editSpecial.hideAddress ?? null,
        eventAddress: editSpecial.eventAddress?.trim() || null,
        visibility: editSpecial.visibility ?? VenueEventVisibility.Public,
        isTicketed: editSpecial.isTicketed ?? false,
        ticketPriceCents: editSpecial.isTicketed ? ticketPriceCents : null,
      } as VenueSpecialDto

      if (editSpecial.publicId) {
        // Update existing special (may also create new sibling events for additional ranges)
        await adminService.updateVenueSpecial(publicId, editSpecial.publicId, payload)
        // Set by an Eventbrite import; manual picks on a saved event upload right away instead.
        if (pendingFlyerFile) {
          await adminService.uploadEventFlyer(publicId, editSpecial.publicId, pendingFlyerFile)
        }
      } else {
        const savedSpecials = await adminService.createVenueSpecial(publicId, payload)

        if (pendingFlyerFile) {
          for (const special of savedSpecials) {
            await adminService.uploadEventFlyer(publicId, special.publicId, pendingFlyerFile)
          }
        }
      }

      if (pendingFlyerPreviewUrl) {
        URL.revokeObjectURL(pendingFlyerPreviewUrl)
      }
      setPendingFlyerFile(null)
      setPendingFlyerPreviewUrl(null)

      const updatedSpecials = await adminService.getVenueSpecials(publicId)
      setSpecials(updatedSpecials)

      setIsPropertyBarVisible(false)
      setEditSpecial(null)
    } catch (err: any) {
      setSpecialFormError(err?.message || 'Failed to save the event. Please try again.')
    } finally {
      setIsSavingSpecial(false)
    }
  }

  /** Same rule the backend applies on save to set the event's eventbriteEventId. */
  const linkedEventbriteEventId =
    parseEventbriteEventId(editSpecial?.externalLink?.link) ?? parseEventbriteEventId(editSpecial?.externalUrl)

  const updateExternalLink = (patch: Partial<VenueEventExternalLinkDto>) => {
    setEditSpecial(prev => {
      if (!prev) return null
      return {
        ...prev,
        externalLink: {
          name: prev.externalLink?.name ?? '',
          link: prev.externalLink?.link ?? '',
          ...patch,
        },
      }
    })
  }

  const clearExternalLink = () => {
    setEditSpecial(prev => (prev ? { ...prev, externalLink: null } : null))
  }

  const handleDeleteBlast = async () => {
    if (!selectedScheduledBlast) return
    setIsBlastDeleting(true)
    let resState = true
    try {
      await adminService.deleteScheduledBlast(selectedScheduledBlast)
      setBlasts([
        ...blasts.filter(
          (b) => b.publicId !== selectedScheduledBlast
        )
      ])
    } catch (err) {
      console.error('Failed to download event analytics report', err)
      resState = false
    } finally {
      setIsBlastDeleting(false)
    }
    return resState
  }

  const handleDeleteSpecial = async () => {
    if (!publicId || !deleteSpecialTarget) return false
    setIsSpecialDeleting(true)
    let resState = true
    try {
      await adminService.deleteVenueSpecial(publicId, deleteSpecialTarget.publicId)
      setSpecials(prev => prev.filter(s => s.publicId !== deleteSpecialTarget.publicId))
    } catch (err) {
      console.error('Failed to delete special', err)
      resState = false
    } finally {
      setIsSpecialDeleting(false)
    }
    return resState
  }

  const handleReportDownload = async (special: VenueSpecialDto) => {
    if (!publicId) return
    try {
      await adminService.downloadEventAnalyticsReport(special.publicId)
    } catch (err) {
      console.error('Failed to download event analytics report', err)
    }
  }

  const handleChangeVenueMapVisibility = async (e: ChangeEvent<HTMLSelectElement>) => {
    if (!publicId || !venueDetail) return

    const isHidden = e.target.value === 'true'
    if (isHidden === venueDetail.isHidden) return

    setIsMapVisibilityPending(true)
    try {
      const updated = await adminService.setVenueMapVisibility(publicId, isHidden)
      setVenueDetail(updated)
    } catch (err) {
      console.error('Failed to update venue map visibility', err)
    } finally {
      setIsMapVisibilityPending(false)
    }
  }

  const handleConfirmCloseVenue = async () => {
    if (!publicId || !venueDetail) return false
    if (closeConfirmName.trim() !== venueDetail.name.trim()) return false

    setIsClosePending(true)
    try {
      const updated = await adminService.closeVenue(publicId)
      setVenueDetail(updated)
      setCloseConfirmName('')
      return true
    } catch (err) {
      console.error('Failed to close venue', err)
      return false
    } finally {
      setIsClosePending(false)
    }
  }

  const handleReopenVenue = async () => {
    if (!publicId || !venueDetail) return

    setIsReopenPending(true)
    try {
      const updated = await adminService.reopenVenue(publicId)
      setVenueDetail(updated)
    } catch (err) {
      console.error('Failed to reopen venue', err)
    } finally {
      setIsReopenPending(false)
    }
  }

  const handleChangeVenueType = async (e: ChangeEvent<HTMLSelectElement>) => {
    if (!publicId || !venueDetail) return

    const nextType = e.target.value as VenueType
    if (nextType === venueDetail.type) return

    setIsVenueTypePending(true)
    try {
      const updated = await adminService.updateVenueType(publicId, nextType)
      setVenueDetail(updated)
    } catch (err) {
      console.error('Failed to update venue type', err)
    } finally {
      setIsVenueTypePending(false)
    }
  }

  const handleChangeVenueSpotlight = async (spotlightStartDate: string | null, spotlightEndDate: string | null) => {
    if (!publicId) return

    setIsSpotlightPending(true)
    try {
      const updated = await adminService.updateVenueSpotlight(publicId, spotlightStartDate, spotlightEndDate)
      setVenueDetail(updated)
    } catch (err) {
      console.error('Failed to update venue spotlight period', err)
    } finally {
      setIsSpotlightPending(false)
    }
  }

  const handleSaveVenueName = async () => {
    if (!publicId || !venueDetail) return

    const nextName = editedVenueName.trim()
    if (!nextName) {
      setVenueNameError('Name is required')
      return
    }
    if (nextName === venueDetail.name) {
      setVenueNameError(null)
      return
    }

    setIsVenueNamePending(true)
    setVenueNameError(null)
    try {
      const updated = await adminService.updateVenueName(publicId, nextName)
      setVenueDetail(updated)
      setEditedVenueName(updated.name ?? nextName)
    } catch (err) {
      console.error('Failed to update venue name', err)
      setVenueNameError('Failed to save name')
    } finally {
      setIsVenueNamePending(false)
    }
  }

  const tabs: Array<{ key: 'back' | TabKey; label: string }> = useMemo(() => {
    const items: Array<{ key: 'back' | TabKey; label: string }> = [
      { key: 'back', label: '←' },
    ]

    if (isPermissionsLoading) {
      items.push({ key: 'details', label: 'Details' })
      return items
    }

    items.push(
      ...VENUE_DETAIL_TABS.filter(tab => hasPermission(tab.permission)).map(tab => ({
        key: tab.key,
        label: tab.label,
      })),
    )

    return items
  }, [hasPermission, isPermissionsLoading])

  useEffect(() => {
    if (!tabs.some(tab => tab.key === activeTab)) {
      const fallback = tabs.find(tab => tab.key !== 'back')?.key ?? 'details'
      setActiveTab(fallback as TabKey)
    }
  }, [activeTab, tabs])

  const breadcrumbColumns = useMemo(
    () => [
      { label: '', accessor: 'author.imageUrl', type: 'avatar' },
      { label: 'From', accessor: 'author.firstName', type: 'fullName' },
      { label: 'Reactions', accessor: 'reactionCount', type: 'number' },
      { label: 'When', accessor: 'createdAt', type: 'datetime' },
      { label: 'Message', accessor: 'content', type: 'string' },
      { label: 'Flagged', accessor: 'publicId', type: 'breadcrumbModerationFlags' },
    ],
    [],
  )

  const stickerColumns = useMemo(
    () => [
      { label: '', accessor: 'imageUrl', type: 'thumbnail' },
      { label: 'From', accessor: 'author.firstName', type: 'fullName' },
      { label: 'Caption', accessor: 'caption', type: 'text' },
      { label: 'Posted', accessor: 'createdAt', type: 'datetime' },
      { label: 'Expires', accessor: 'expiresAt', type: 'datetime' },
      { label: 'Flagged', accessor: 'isFlagged', type: 'flagged' },
    ],
    [],
  )

  const eventCommentColumns = useMemo(
    () => [
      { label: '', accessor: 'author.imageUrl', type: 'userAvatar' },
      { label: 'Author', accessor: 'author.lastName', type: 'fullNameAndTelephone' },
      { label: 'Comment', accessor: 'content', type: 'markdown' },
      { label: 'Event', accessor: 'event.name', type: 'text' as any },
      { label: 'Likes', accessor: 'likeCount', type: 'number' },
      { label: 'Created', accessor: 'createdAt', type: 'datetime' },
    ],
    [],
  )

  const primaryEventRange = editSpecial?.eventDateRanges?.[0]
  const primaryEventEndReference = primaryEventRange?.eventEndDate || primaryEventRange?.eventStartDate
  const hasPrimaryEventEnded = !!primaryEventEndReference && dayjs(primaryEventEndReference).isBefore(dayjs())
  const isSpotlightStillOpen = !editSpecial?.endDate || dayjs(editSpecial.endDate).isAfter(dayjs())
  const showEventEndedSpotlightWarning = hasPrimaryEventEnded && isSpotlightStillOpen

  return (
    <div className="venue-detail-page">
      <div className="venue-detail-header">
        <div className="left-info-wrapper">
          <div className="avatar-wrapper">
            <Avatar
              url={venueDetail?.logoUrl}
              firstName={venueDetail?.name}
              lastName={venueDetail?.name}
            />
            <button
              className="change-avatar"
              title="Edit venue`s logo"
              onClick={() => setIsLogoEditModalOpen(true)}
            >
              <Icon name={'Upload'} />
            </button>
          </div>
          <h2>
            {venueDetail?.name ?? 'Venue Detail'}
            {venueDetail?.isClosed ? <span className="venue-closed-badge" style={{ marginLeft: 12 }}>Closed</span> : null}
          </h2>
        </div>
      </div>

      <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {tabs.map(tab => {
            const classNames = ['tab']

            if (tab.key === activeTab) {
              classNames.push('active')
            }

            return (
              <button key={tab.key} type="button" className={classNames.join(' ')} onClick={() => handleTabChange(tab.key)}>
                {tab.label}
              </button>
            )
          })}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {activeTab === 'details' && (
            <button className="styled-button" onClick={() => setIsBlastModalOpen(true)} style={{ padding: '8px 16px', fontSize: '14px' }}>
              Send blast
            </button>
          )}
          {activeTab === 'specials' && (
            <button className="styled-button" onClick={handleAddSpecial} style={{ padding: '8px 16px', fontSize: '14px' }}>
              Add Special
            </button>
          )}
        </div>
      </div>

      {error && !isLoading && <div className="page-error">{error}</div>}

      {!isLoading && !error && venueDetail && (
        <div className="venue-detail-content">
          {activeTab === 'details' && (
            <>
              {venueDetail.isClosed && (
                <div className="venue-closed-banner">
                  <div>
                    <strong>This venue is closed.</strong>
                    {venueDetail.closedAt ? ` Closed at ${new Date(venueDetail.closedAt).toLocaleString()}.` : ''}
                    {' '}It is hidden from the app map and visit flows.
                  </div>
                  <button
                    type="button"
                    className="styled-button sm"
                    onClick={() => void handleReopenVenue()}
                    disabled={isReopenPending}
                  >
                    <span>{isReopenPending ? 'Reopening…' : 'Reopen venue'}</span>
                  </button>
                </div>
              )}
              <div className="venue-detail-overview">
              <div className="venue-detail-media">
                <div className="venue-detail-photo">{venueDetail?.photoReference ? <img src={photoUrl ?? ''} alt="" /> : <></>}</div>
                <div>
                  <h3 style={{ marginBottom: 6 }}>Venue banner</h3>
                  <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Custom cover photo for the venue bottom sheet header. Recommended aspect ratio ~2.7:1
                    (e.g. 1200x450px) — it's shown cropped to fill a wide banner, so a taller/narrower photo
                    may get cropped top/bottom. Falls back to the logo header when not set.
                  </p>
                  {venueDetail?.bannerUrl ? (
                    <div
                      className="thumbnail"
                      style={{
                        backgroundImage: `url(${venueDetail.bannerUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        width: '100%',
                        height: '140px',
                        borderRadius: '12px',
                        marginBottom: '12px',
                      }}
                    />
                  ) : (
                    <p style={{ marginBottom: '12px' }}>No banner uploaded</p>
                  )}
                  <ImageDropzone
                    maxMbSize={5}
                    onFileAccepted={async file => {
                      if (!publicId) {
                        return
                      }

                      try {
                        const bannerUrl = await adminService.uploadVenueBanner(publicId, file)
                        setVenueDetail(prev => (prev ? { ...prev, bannerUrl } : prev))
                      } catch (err) {
                        console.log('Failed to upload venue banner', err)
                      }
                    }}
                  />
                  {venueDetail?.bannerUrl && (
                    <button
                      type="button"
                      className="styled-button cancel"
                      style={{ marginTop: '12px' }}
                      onClick={async () => {
                        if (!publicId) {
                          return
                        }

                        try {
                          await adminService.deleteVenueBanner(publicId)
                          setVenueDetail(prev => (prev ? { ...prev, bannerUrl: null } : prev))
                        } catch (err) {
                          console.log('Failed to delete venue banner', err)
                        }
                      }}
                    >
                      Remove banner
                    </button>
                  )}
                </div>
              </div>
              <div className="venue-detail-info">
                <div className="form-group form-item venue-name-editor">
                  <label>Name</label>
                  <div className="venue-name-editor-row">
                    <input
                      value={editedVenueName}
                      onChange={e => {
                        setEditedVenueName(e.target.value)
                        if (venueNameError) setVenueNameError(null)
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          void handleSaveVenueName()
                        }
                      }}
                      disabled={isVenueNamePending}
                      maxLength={255}
                    />
                    <button
                      type="button"
                      className="styled-button sm"
                      onClick={() => void handleSaveVenueName()}
                      disabled={
                        isVenueNamePending ||
                        editedVenueName.trim().length === 0 ||
                        editedVenueName.trim() === (venueDetail.name ?? '')
                      }
                    >
                      <span>{isVenueNamePending ? 'Saving…' : 'Save'}</span>
                    </button>
                  </div>
                  {venueNameError && <p className="venue-name-editor-error">{venueNameError}</p>}
                </div>
                <div className="venue-address-row">
                  <FormItem label="Address" value={venueDetail.address} />
                  <button
                    type="button"
                    className="styled-button sm"
                    onClick={() => setIsLocationEditModalOpen(true)}
                  >
                    <span>Edit location</span>
                  </button>
                </div>
                <LocationView
                  latitude={venueDetail.latitude}
                  longitude={venueDetail.longitude}
                  northeastLatitude={venueDetail.northeastLatitude}
                  northeastLongitude={venueDetail.northeastLongitude}
                  southwestLatitude={venueDetail.southwestLatitude}
                  southwestLongitude={venueDetail.southwestLongitude}
                  renderTrigger={open => (
                    <FormItem label="Location" className="clickable" value={formatCoordinates(venueDetail.latitude, venueDetail.longitude)} onClick={open} />
                  )}
                />
                <div className="form-group form-item">
                  <label>Type</label>
                  <select
                    value={venueDetail.type}
                    onChange={handleChangeVenueType}
                    disabled={isVenueTypePending}
                  >
                    {Object.values(VenueType).map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group form-item">
                  <label>Sponsored spotlight period</label>
                  <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                    While active and within the spotlight search radius, this venue is promoted to the
                    &quot;Sponsored&quot; ticker card regardless of other activity.
                  </div>
                  <AdminDateRangeCalendarRow
                    startDate={venueDetail.spotlightStartDate ?? null}
                    endDate={venueDetail.spotlightEndDate ?? null}
                    onChange={handleChangeVenueSpotlight}
                  />
                  {isSpotlightPending ? <span className="feature-flag-subtitle">Saving…</span> : null}
                </div>
                <div className="form-group form-item">
                  <label>Map &amp; discovery</label>
                  <select
                    value={String(venueDetail.isHidden ?? false)}
                    onChange={handleChangeVenueMapVisibility}
                    disabled={isMapVisibilityPending || venueDetail.isClosed}
                  >
                    <option value="false">Visible on map</option>
                    <option value="true">Hidden from map</option>
                  </select>
                </div>
                <div className="form-group form-item">
                  <label>Venue status</label>
                  <div className="venue-status-actions">
                    {venueDetail.isClosed ? (
                      <button
                        type="button"
                        className="styled-button sm"
                        onClick={() => void handleReopenVenue()}
                        disabled={isReopenPending}
                      >
                        <span>{isReopenPending ? 'Reopening…' : 'Reopen venue'}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="styled-button sm"
                        onClick={() => {
                          setCloseConfirmName('')
                          setIsCloseModalOpen(true)
                        }}
                      >
                        <span>Close venue</span>
                      </button>
                    )}
                  </div>
                </div>
                <FormItem label="Public ID" value={venueDetail.publicId} />
                <FormItem label="Created" value={venueDetail.createdAt} type="datetime" />
                {venueDetail.suggestedByName && (
                  <FormItem label="Suggested by" value={venueDetail.suggestedByName} />
                )}
                {publicId && (
                  <VenueOpeningHoursEditor
                    venuePublicId={publicId}
                    venueDetail={venueDetail}
                    onUpdated={setVenueDetail}
                  />
                )}
              </div>
              <div className="venue-detail-counts">
                <FormItem label="Visits" value={venueDetail.visitCount}></FormItem>
                <FormItem label="Peeks" value={venueDetail.peekCount}></FormItem>
                <FormItem label="Specials" value={venueDetail.specialsCount}></FormItem>
                <FormItem label="Total visit duration" value={venueDetail.totalVisitDuration} type="interval"></FormItem>
                <FormItem
                  label="Average visit duration"
                  value={(venueDetail.totalVisitDuration ?? 0) > 0 ? venueDetail.totalVisitDuration! / venueDetail.visitCount! : null}
                  type="interval"
                ></FormItem>
              </div>
            </div>
            </>
          )}

          {activeTab === 'socials' && (
            <div className="venue-detail-socials">
              <FormItem label="TikTok" value={venueDetail.tikTokHandle ?? '-'} />
              <FormItem label="Instagram" value={venueDetail.instagramHandle ?? '-'} />
              <FormItem label="Facebook" value={venueDetail.facebookHandle ?? '-'} />
            </div>
          )}

          {activeTab === 'visits' && (
            <div className="venue-detail-visits">
              <Table
                id="venue-detail-visits"
                name="Visits"
                keyField="publicId"
                initialSortColumn="checkedInAt"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
                  { label: 'Name/Telephone', accessor: 'user.firstName', type: 'fullNameAndTelephone' },
                  { label: 'Check-in', accessor: 'checkedInAt', type: 'datetime' },
                  { label: 'Visit duration', accessor: 'secondsInVenue', type: 'interval' },
                  { label: 'Messages sent', accessor: 'messagesSent', type: 'number' },
                  { label: 'Messages received', accessor: 'messagesReceived', type: 'number' },
                  { label: 'Checkout type', accessor: 'checkoutType', type: 'checkoutType' },
                ]}
                sourceData={visits}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={(visit: VisitDto) => setSelectedVisit(visit.publicId)}
                disableGlobalSearch={true}
              />
            </div>
          )}

          {activeTab === 'blasts' && (
            <div className="venue-detail-blasts">
              <Table
                id="venue-detail-blasts"
                name="Scheduled Blasts"
                keyField="publicId"
                disableClientSort
                sortDisabled
                columns={[
                  { label: 'Scheduled to', accessor: 'scheduledDate', type: 'datetime' },
                  { label: 'Scheduled to (Central Time)', accessor: 'scheduledDateCentral', type: 'datetime' },
                  { label: 'Status', accessor: 'status', type: 'enum' },
                  { label: 'Radius in miles', accessor: 'radiusMiles', type: 'number' },
                  { label: 'Deep Link Target', accessor: 'deepLinkTarget', type: 'enum' },
                  { label: 'Message', accessor: 'message', type: 'string' },
                ]}
                sourceData={blasts}
                isPropertyBarVisible={false}
                filterTerms=""
                highlightRows
                options={[
                  {
                    label: 'Edit',
                    onClick: (blast: ScheduledBlastListItemDto) => {
                      if (blast.status !== ScheduledBlastStatus.Pending) {
                        alert('Only pending blasts can be edited.')
                        return
                      }
                      setEditingBlast(blast)
                    },
                  },
                ]}
                onDelete={
                  (blast: ScheduledBlastListItemDto) => setSelectedScheduledBlast(blast.publicId)
                }
                disableGlobalSearch={true}
              />
            </div>
          )}

          {activeTab === 'map' && (
            <div className="venue-detail-map-tab">
              {venueDetail.latitude != null && venueDetail.longitude != null ? (
                <div className="map-tab-static">
                  <LocationMap
                    latitude={venueDetail.latitude}
                    longitude={venueDetail.longitude}
                    northeastLatitude={venueDetail.northeastLatitude}
                    northeastLongitude={venueDetail.northeastLongitude}
                    southwestLatitude={venueDetail.southwestLatitude}
                    southwestLongitude={venueDetail.southwestLongitude}
                    className="venue-detail-map-inline"
                    style={{ width: '100%', height: '100%', borderRadius: 8 }}
                  />
                  <div className="map-tab-actions">
                    <LocationView
                      latitude={venueDetail.latitude}
                      longitude={venueDetail.longitude}
                      northeastLatitude={venueDetail.northeastLatitude}
                      northeastLongitude={venueDetail.northeastLongitude}
                      southwestLatitude={venueDetail.southwestLatitude}
                      southwestLongitude={venueDetail.southwestLongitude}
                    />
                  </div>
                </div>
              ) : (
                <div className="map-tab-empty">Location information is unavailable for this venue.</div>
              )}
            </div>
          )}

          {activeTab === 'specials' && (
            <div>
              <Table
                id="venue-detail-specials"
                name="Specials"
                keyField="publicId"
                initialSortColumn="endDate"
                initialSortOrder="desc"
                columns={[
                  { label: 'Flyer', accessor: 'flyerUrl', type: 'thumbnail' },
                  { label: 'Name', accessor: 'name' },
                  { label: 'Content', accessor: 'content', type: 'markdown' },
                  { label: 'Day of week', accessor: 'dayOfWeek', type: 'dayOfWeek' },
                  { label: 'Spotlight start date', accessor: 'startDate', type: 'datetime' },
                  { label: 'Spotlight end date', accessor: 'endDate', type: 'datetime' },
                  { label: 'Event start date', accessor: 'eventStartDate', type: 'datetime' },
                  { label: 'Event end date', accessor: 'eventEndDate', type: 'datetime' },
                  { label: 'Reports', accessor: 'eventEndDate', type: 'reportDownload' },
                ]}
                sourceData={specials}
                isPropertyBarVisible={isPropertyBarVisible}
                filterTerms=""
                onRowClick={handleSpecialRowClick}
                onReportDownload={handleReportDownload}
                onDelete={(special: VenueSpecialDto) => setDeleteSpecialTarget(special)}
                disableGlobalSearch={true}
              />
            </div>
          )}

          {activeTab === 'peeks' && (
            <Table
              id="venue-detail-peeks"
              name="Peeks"
              keyField="_index"
              initialSortColumn="createdAt"
              initialSortOrder="desc"
              columns={[
                { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
                { label: 'Name/Telephone', accessor: 'user.firstName', type: 'fullNameAndTelephone' },
                { label: 'Type', accessor: 'type', type: 'enum' },
                { label: 'Location', accessor: 'latitude', type: 'coordinates' },
                { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              ]}
              sourceData={peeks}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={null}
              disableGlobalSearch={true}
              onCoordinatesClick={handleCoordinatesClick}
            />
          )}

          {activeTab === 'breadcrumbs' && (
            <div className="venue-detail-breadcrumbs">
              {breadcrumbsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={breadcrumbsPagination.page}
                    totalPages={breadcrumbsPagination.totalPages}
                    onPageChange={setBreadcrumbsPage}
                  />
                </div>
              )}
              <Table
                id="venue-detail-breadcrumbs"
                name="Breadcrumbs"
                keyField="publicId"
                initialSortColumn={breadcrumbsSortBy}
                initialSortOrder={breadcrumbsSortOrder}
                columns={breadcrumbColumns}
                sourceData={breadcrumbs}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={(row: AdminVenueBreadcrumbDto) => setSelectedBreadcrumbPublicId(row.publicId)}
                onSortChange={(sortBy, sortOrder) => {
                  setBreadcrumbsSortBy(sortBy)
                  setBreadcrumbsSortOrder(sortOrder)
                  setBreadcrumbsPage(1)
                }}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'stickers' && (
            <div className="venue-detail-breadcrumbs">
              {stickersPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={stickersPagination.page}
                    totalPages={stickersPagination.totalPages}
                    onPageChange={setStickersPage}
                  />
                </div>
              )}
              <Table
                id="venue-detail-stickers"
                name="Stickers"
                keyField="publicId"
                initialSortColumn={stickersSortBy}
                initialSortOrder={stickersSortOrder}
                columns={stickerColumns}
                sourceData={stickers}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={(row: AdminStickerDto) => setSelectedStickerPublicId(row.publicId)}
                onSortChange={(sortBy, sortOrder) => {
                  setStickersSortBy(sortBy)
                  setStickersSortOrder(sortOrder)
                  setStickersPage(1)
                }}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'event-comments' && (
            <div className="venue-detail-event-comments">
              {eventCommentsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={eventCommentsPagination.page}
                    totalPages={eventCommentsPagination.totalPages}
                    onPageChange={setEventCommentsPage}
                  />
                </div>
              )}
              <Table
                id="venue-detail-event-comments"
                name="Event comments"
                keyField="publicId"
                initialSortColumn={eventCommentsSortBy}
                initialSortOrder={eventCommentsSortOrder}
                columns={eventCommentColumns}
                sourceData={eventComments}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={(row: AdminVenueEventCommentListItemDto) => setSelectedCommentPublicId(row.publicId)}
                onSortChange={(sortBy, sortOrder) => {
                  setEventCommentsSortBy(sortBy)
                  setEventCommentsSortOrder(sortOrder)
                  setEventCommentsPage(1)
                }}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'vibes' && (
            <div className="venue-detail-vibes">
              {vibeLogsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={vibeLogsPagination.page}
                    totalPages={vibeLogsPagination.totalPages}
                    onPageChange={setVibeLogsPage}
                  />
                </div>
              )}
              <Table
                id="venue-detail-vibes"
                name="Vibe checks"
                keyField="publicId"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: 'Set at', accessor: 'createdAt', type: 'datetime' },
                  { label: 'User', accessor: 'affectedUserFullName', type: 'text' as any },
                  { label: 'Emoji', accessor: 'data.emoji', type: 'text' as any },
                  { label: 'Comment', accessor: 'comment', type: 'text' as any },
                ]}
                sourceData={vibeLogs}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'intentions' && (
            <div className="venue-detail-intentions">
              {intentionLogsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={intentionLogsPagination.page}
                    totalPages={intentionLogsPagination.totalPages}
                    onPageChange={setIntentionLogsPage}
                  />
                </div>
              )}
              <Table
                id="venue-detail-intentions"
                name="Intentions"
                keyField="publicId"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: 'Set at', accessor: 'createdAt', type: 'datetime' },
                  { label: 'User', accessor: 'affectedUserFullName', type: 'text' as any },
                  { label: 'Intention', accessor: 'data.name', type: 'text' as any },
                  { label: 'Prompt', accessor: 'data.prompt', type: 'text' as any },
                  { label: 'Emoji', accessor: 'data.emoji', type: 'text' as any },
                ]}
                sourceData={intentionLogs}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'visit-plans' && (
            <div className="venue-detail-visit-plans">
              {visitPlansPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={visitPlansPagination.page}
                    totalPages={visitPlansPagination.totalPages}
                    onPageChange={setVisitPlansPage}
                  />
                </div>
              )}
              <Table
                id="venue-detail-visit-plans"
                name="Visit plans"
                keyField="publicId"
                initialSortColumn="plannedDate"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
                  { label: 'User', accessor: 'user.lastName', type: 'fullNameAndTelephone' },
                  { label: 'Planned date', accessor: 'plannedDate', type: 'date' },
                  { label: 'Time slot', accessor: 'timeSlot', type: 'enum', enumName: 'VenueVisitPlanTimeSlot' },
                  { label: 'Active', accessor: 'isActive', type: 'text' as any },
                  { label: 'Passed', accessor: 'isPassed', type: 'text' as any },
                  { label: 'Created', accessor: 'createdAt', type: 'datetime' },
                  { label: 'Calendar', accessor: 'calendarStatusLabel', type: 'text' as any },
                ]}
                sourceData={visitPlans}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
                options={[
                  {
                    label: 'Sync to Google Calendar',
                    onClick: handleSyncVisitPlanToGoogleCalendar,
                  },
                ]}
              />
            </div>
          )}

          {activeTab === 'event-rsvps' && (
            <div className="venue-detail-event-rsvps">
              <div className="venue-detail-event-rsvps__toolbar">
                <select
                  className="map-filter-input"
                  value={eventRsvpsEventFilter}
                  onChange={e => {
                    setEventRsvpsEventFilter(e.target.value)
                    setEventRsvpsPage(1)
                  }}
                >
                  <option value="">All events</option>
                  {specials.map(special => (
                    <option key={special.publicId} value={special.publicId}>
                      {special.name}
                      {special.eventStartDate ? ` (${new Date(special.eventStartDate).toLocaleDateString()})` : ''}
                    </option>
                  ))}
                </select>
                <button type="button" className="styled-button sm" onClick={() => setIsRsvpBlastModalOpen(true)}>
                  Send blast
                </button>
              </div>
              {eventRsvpsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={eventRsvpsPagination.page}
                    totalPages={eventRsvpsPagination.totalPages}
                    onPageChange={setEventRsvpsPage}
                  />
                </div>
              )}
              <Table
                id="venue-detail-event-rsvps"
                name="Event RSVPs"
                keyField="id"
                initialSortColumn="rsvpedAt"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
                  { label: 'User', accessor: 'user.lastName', type: 'fullNameAndTelephone' },
                  { label: 'Event', accessor: 'event.name', type: 'text' as any },
                  { label: 'RSVPed at', accessor: 'rsvpedAt', type: 'datetime' },
                ]}
                sourceData={eventRsvps}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}
        </div>
      )}
      <Modal
        isOpen={!!selectedCoordinates}
        onRequestClose={() => setSelectedCoordinates(null)}
        className="location-view-popup"
        overlayClassName="location-view-overlay"
      >
        {selectedCoordinates && (
          <div style={{ padding: '20px', width: '80vw', height: '80vh' }}>
            <LocationMap
              latitude={selectedCoordinates.latitude}
              longitude={selectedCoordinates.longitude}
              className="location-view-map"
              style={{ width: '100%', height: '100%', borderRadius: 4 }}
            />
          </div>
        )}
      </Modal>
      <Modal
        isOpen={!!selectedVisit && !!visitDetail}
        onRequestClose={() => setSelectedVisit(null)}
        className="visit-map-popup"
        overlayClassName="modal-overlay"
        closeTimeoutMS={300}
      >
        {visitDetail && (
          <div className="visit-map-popup-content">
            <div className="visit-map-popup-map-container">
              <VisitMap
                latitude={visitDetail.venue.latitude}
                longitude={visitDetail.venue.longitude}
                northeastLatitude={visitDetail.venue.northeastLatitude}
                northeastLongitude={visitDetail.venue.northeastLongitude}
                southwestLatitude={visitDetail.venue.southwestLatitude}
                southwestLongitude={visitDetail.venue.southwestLongitude}
                pings={visitDetail.pings || null}
              />
            </div>
          </div>
        )}
      </Modal>
      <VenueLogoModal
        venuePublicId={venueDetail?.publicId ?? ''}
        logoUrl={venueDetail?.logoUrl ?? undefined}
        logoZoomSize={venueDetail?.mapLogoZoom}
        hasCustomLogo={venueDetail?.isPartnerVenue}
        isOpen={isLogoEditModalOpen}
        onClose={() => setIsLogoEditModalOpen(false)}
      />
      {venueDetail && (
        <EditVenueLocationModal
          venuePublicId={venueDetail.publicId}
          latitude={venueDetail.latitude}
          longitude={venueDetail.longitude}
          address={venueDetail.address}
          postalCode={venueDetail.postalCode}
          northeastLatitude={venueDetail.northeastLatitude}
          northeastLongitude={venueDetail.northeastLongitude}
          southwestLatitude={venueDetail.southwestLatitude}
          southwestLongitude={venueDetail.southwestLongitude}
          isOpen={isLocationEditModalOpen}
          onClose={() => setIsLocationEditModalOpen(false)}
          onSaved={updated => setVenueDetail(updated)}
        />
      )}
      <ConfirmationModal
        isOpen={!!selectedScheduledBlast}
        onClose={() => setSelectedScheduledBlast(null)}
        onConfirm={handleDeleteBlast}
        title={'Confirm Blast Delete'}
        isLoading={isBlastDeleting}
      >
        <p>
          Are you sure you want to delete this scheduled blast?
          <br/>
          Once deleted, it will not be sent at the scheduled time.
        </p>
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={!!deleteSpecialTarget}
        onClose={() => setDeleteSpecialTarget(null)}
        onConfirm={handleDeleteSpecial}
        title="Delete special"
        confirmLabel="Delete"
        isLoading={isSpecialDeleting}
      >
        <p>
          Are you sure you want to delete <strong>{deleteSpecialTarget?.name}</strong>?
          <br/>
          Its RSVPs, comments, likes, tickets and partnered hotspots will be deleted too. This cannot be undone.
        </p>
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={isCloseModalOpen}
        onClose={() => {
          setIsCloseModalOpen(false)
          setCloseConfirmName('')
        }}
        onConfirm={handleConfirmCloseVenue}
        title="Close venue"
        confirmLabel="Close venue"
        isLoading={isClosePending}
        confirmDisabled={!venueDetail || closeConfirmName.trim() !== venueDetail.name.trim()}
      >
        <p>
          Closing <strong>{venueDetail?.name}</strong> will hide it from the app map and lists,
          check out everyone currently at the venue, and cancel all visit plans and RSVPs.
        </p>
        <p>Type the venue name to confirm:</p>
        <input
          value={closeConfirmName}
          onChange={e => setCloseConfirmName(e.target.value)}
          placeholder={venueDetail?.name ?? ''}
          disabled={isClosePending}
          style={{ width: '100%', marginTop: 8 }}
        />
      </ConfirmationModal>
      <Modal
        isOpen={isPropertyBarVisible && !!editSpecial}
        onRequestClose={handleCancelEditSpecial}
        ariaHideApp={false}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        className="modal-popup venue-special-popup"
        overlayClassName="modal-overlay"
      >
        {editSpecial && (
          <div className="venue-special-popup-content modal-content-wrapper">
            <h2>{editSpecial.publicId ? 'Edit Special' : 'New Special'}</h2>

            {specialFormError && <div className="error-message">{specialFormError}</div>}

            <div className="modal-body-content">
              {features[AppFeature.Eventbrite] && (
              <div className="property-bar-field-group">
                <h3 className="property-bar-field-group__title eventbrite-import-title">
                  <img src="/images/eventbrite.png" alt="" />
                  Import from Eventbrite
                </h3>
                <p className="property-bar-field-group__description">
                  Pick one of Ivy's Eventbrite events, or paste any Eventbrite event link, to fill in the name,
                  description, schedule, flyer and ticket link. Review the fields below, then save as usual.
                </p>
                <select
                  className="eventbrite-event-picker"
                  value=""
                  disabled={!eventbriteEvents?.length || isImportingEventbrite}
                  onChange={e => {
                    const picked = eventbriteEvents?.find(event => event.eventbriteId === e.target.value)
                    if (!picked) return
                    setEventbriteLink(picked.url)
                    handleImportEventbrite(picked.url)
                  }}
                >
                  <option value="">
                    {eventbriteEvents === null
                      ? 'Loading Eventbrite events…'
                      : eventbriteEvents.length
                        ? `Choose from Eventbrite (${eventbriteEvents.length} upcoming)`
                        : 'No upcoming Eventbrite events'}
                  </option>
                  {eventbriteEvents?.map(event => {
                    const linkedHere = event.linkedIvyEvents.some(linked => linked.venuePublicId === publicId)
                    const linkedVenues = event.linkedIvyEvents.map(linked => linked.venueName)
                    const details = [
                      event.eventStartDate ? formatEventbriteStart(event.eventStartDate, event.timezone) : null,
                      event.status === 'draft' ? 'Draft' : null,
                      !event.listed ? 'Private' : null,
                      linkedHere ? 'Already at this venue' : linkedVenues.length ? `On Ivy at ${linkedVenues.join(', ')}` : null,
                    ].filter(Boolean)
                    return (
                      <option key={event.eventbriteId} value={event.eventbriteId}>
                        {event.name || 'Untitled event'}
                        {details.length ? ` — ${details.join(' · ')}` : ''}
                      </option>
                    )
                  })}
                </select>
                {eventbriteEventsError && <p className="eventbrite-event-picker__error">{eventbriteEventsError}</p>}
                {/* Inline layout: the modal's generic form/button rules otherwise squeeze this row. */}
                <div
                  className="eventbrite-import-row"
                  style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4 }}
                >
                  <input
                    className="map-filter-input"
                    style={{ flex: '1 1 auto', minWidth: 0, width: 'auto', height: 40, padding: '0 12px', fontSize: 14 }}
                    type="text"
                    placeholder="https://www.eventbrite.com/e/…"
                    value={eventbriteLink}
                    onChange={e => setEventbriteLink(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleImportEventbrite()
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="styled-button sm"
                    style={{ flex: '0 0 auto', width: 'auto', height: 40, padding: '0 20px', whiteSpace: 'nowrap' }}
                    disabled={!eventbriteLink.trim() || isImportingEventbrite}
                    onClick={() => handleImportEventbrite()}
                  >
                    {isImportingEventbrite ? 'Importing…' : 'Import'}
                  </button>
                </div>
                {eventbriteImport && (
                  <div className="eventbrite-import-summary">
                    <p>
                      Imported <strong>{eventbriteImport.name}</strong>
                      {eventbriteImport.status !== 'live' && ` (${eventbriteImport.status} on Eventbrite)`}.
                    </p>
                    <p>
                      {eventbriteImport.isOnline
                        ? 'Online event.'
                        : [eventbriteImport.venueName, eventbriteImport.address].filter(Boolean).join(' · ') ||
                          'No venue on Eventbrite.'}
                    </p>
                    <p>{formatEventbritePrice(eventbriteImport)}</p>
                    {!eventbriteImport.isOnline && eventbriteImport.address && (
                      <button
                        type="button"
                        className="styled-button cancel sm"
                        onClick={() =>
                          setEditSpecial(prev => (prev ? { ...prev, eventAddress: eventbriteImport.address } : null))
                        }
                      >
                        Use as custom address
                      </button>
                    )}
                  </div>
                )}
              </div>
              )}

              <TextInput
                label="Name"
                name="name"
                type="text"
                required={true}
                value={editSpecial.name || ''}
                onChange={(val: string) => setEditSpecial(prev => (prev ? { ...prev, name: val } : null))}
              />
              <TextInput
                label="Content"
                name="content"
                type="textArea"
                required={true}
                value={editSpecial.content || ''}
                onChange={(val: string) => setEditSpecial(prev => (prev ? { ...prev, content: val } : null))}
              />

              <div className="property-bar-field-group">
                <h3 className="property-bar-field-group__title">Event schedule</h3>
                <p className="property-bar-field-group__description">
                  The real date(s) and time(s) of the event itself — used for RSVPs, analytics, and whether the
                  event shows as upcoming, in progress, or already finished. Independent of when it's promoted
                  (see Spotlight below). Times are entered in Central Time.
                </p>
                <AdminEventDateRangesField
                  ranges={editSpecial.eventDateRanges || []}
                  onChange={(ranges: EventDateRangeDto[]) => setEditSpecial(prev => (prev ? { ...prev, eventDateRanges: ranges } : null))}
                />
              </div>

              <div className="property-bar-field-group">
                <h3 className="property-bar-field-group__title">Spotlight visibility</h3>
                <p className="property-bar-field-group__description">
                  Controls when the event is <em>promoted</em> in the app (specials feed, map pins, and
                  notifications) — it does not need to match the event schedule above. Outside this window the
                  event stays hidden, even while it's actually happening. Times are entered in Central Time.
                </p>
                {showEventEndedSpotlightWarning && (
                  <p className="admin-event-date-ranges-field__warning">
                    This event has already ended, but its spotlight window is still open. Consider setting a
                    spotlight end date so it stops being promoted.
                  </p>
                )}
                <AdminDateRangeCalendarRow
                  startLabel="Spotlight starts"
                  endLabel="Spotlight ends"
                  startDate={editSpecial.startDate || null}
                  endDate={editSpecial.endDate || null}
                  onChange={(startDate, endDate) => setEditSpecial(prev => (prev ? { ...prev, startDate, endDate } : null))}
                />
              </div>

              <div className="property-bar-field-group">
                <h3 className="property-bar-field-group__title">Visibility</h3>
                <p className="property-bar-field-group__description">
                  Internal only events are shown on mobile to internal users only.
                </p>
                <select
                  className="map-filter-input"
                  value={editSpecial.visibility ?? VenueEventVisibility.Public}
                  onChange={e =>
                    setEditSpecial(prev =>
                      prev ? { ...prev, visibility: e.target.value as VenueEventVisibility } : null
                    )
                  }
                >
                  {Object.values(VenueEventVisibility).map(visibility => (
                    <option key={visibility} value={visibility}>
                      {VENUE_EVENT_VISIBILITY_LABELS[visibility]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="property-bar-field-group">
                <h3 className="property-bar-field-group__title">Tickets</h3>
                <p className="property-bar-field-group__description">
                  Ticketed events let users buy a ticket in the app.
                </p>
                {!features[AppFeature.Payments] && (
                  <p className="property-bar-field-group__description">
                    <strong>Square Tickets is switched off in Feature Flags</strong> — users see ticketed events as regular
                    RSVP events until they are turned back on.
                  </p>
                )}
                <div className="form-group row">
                  <label>Sell tickets</label>
                  <ToggleInput
                    value={!!editSpecial.isTicketed}
                    onChange={() =>
                      setEditSpecial(prev => (prev ? { ...prev, isTicketed: !prev.isTicketed } : null))
                    }
                  />
                </div>
                {editSpecial.isTicketed && (
                  <div className="form-group">
                    <label>Ticket price (USD)</label>
                    <input
                      className="map-filter-input"
                      type="text"
                      inputMode="decimal"
                      placeholder="12.50"
                      value={ticketPriceInput}
                      onChange={e => setTicketPriceInput(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="property-bar-field-group">
                <h3 className="property-bar-field-group__title">Event address</h3>
                <p className="property-bar-field-group__description">
                  Override or hide the venue address for this event. Use a custom address for off-site
                  locations (e.g. festivals), or hide the address when it is only revealed after ticket purchase.
                </p>
                <TextInput
                  label="Custom address"
                  name="eventAddress"
                  type="text"
                  value={editSpecial.eventAddress || ''}
                  onChange={(val: string) =>
                    setEditSpecial(prev => (prev ? { ...prev, eventAddress: val || null } : null))
                  }
                />
                <div className="form-group row" style={{ marginTop: '12px' }}>
                  <label>Hide address</label>
                  <ToggleInput
                    value={!!editSpecial.hideAddress}
                    onChange={() =>
                      setEditSpecial(prev =>
                        prev ? { ...prev, hideAddress: prev.hideAddress ? null : true } : null
                      )
                    }
                  />
                </div>
              </div>

              <div className="property-bar-field-group">
                <h3 className="property-bar-field-group__title">External link</h3>
                <p className="property-bar-field-group__description">
                  Optional link to a third-party registration or ticket page for the real-world event.
                </p>
                <p className="property-bar-field-group__description">
                  Eventbrite: paste the event's <strong>ivy-event</strong> tracking link (Marketing → Tracking
                  links). The app switches it to <strong>ivy-hotspot</strong> or <strong>ivy-push</strong> when the
                  event is opened from the hotspot or a push, so create all three links on the event.
                </p>
                <div className="venue-special-external-link">
                  <TextInput
                    label="Name"
                    name="externalLinkName"
                    type="text"
                    value={editSpecial.externalLink?.name || ''}
                    onChange={(val: string) => updateExternalLink({ name: val })}
                  />
                  <TextInput
                    label="Link"
                    name="externalLinkUrl"
                    type="text"
                    value={editSpecial.externalLink?.link || ''}
                    onChange={(val: string) => updateExternalLink({ link: val })}
                  />
                  {(editSpecial.externalLink?.name || editSpecial.externalLink?.link) && (
                    <button
                      type="button"
                      className="styled-button cancel sm"
                      onClick={clearExternalLink}
                    >
                      Clear link
                    </button>
                  )}
                </div>
                {linkedEventbriteEventId && (
                  <p className="property-bar-field-group__description">
                    Linked Eventbrite event <strong>#{linkedEventbriteEventId}</strong>.{' '}
                    {editSpecial.isTicketed
                      ? 'Users who connected Eventbrite see their Eventbrite tickets for this event in the app.'
                      : 'Eventbrite tickets are shown to users only while Sell tickets is on.'}
                  </p>
                )}
              </div>

              <div className="property-bar-field-group">
                <h3 className="property-bar-field-group__title">Event flyer</h3>
                <p className="property-bar-field-group__description">
                  Cover image shown on the event card. This is separate from the venue logo.
                </p>
                {(pendingFlyerPreviewUrl || editSpecial.flyerUrl) ? (
                  <div
                    className="thumbnail"
                    style={{
                      backgroundImage: `url(${pendingFlyerPreviewUrl || editSpecial.flyerUrl})`,
                      width: '100%',
                      height: '180px',
                      marginBottom: '12px',
                    }}
                  />
                ) : (
                  <p style={{ marginBottom: '12px' }}>No flyer uploaded</p>
                )}
                <ImageDropzone
                  maxMbSize={5}
                  onFileAccepted={async file => {
                    if (!publicId) {
                      return
                    }

                    if (editSpecial.publicId) {
                      try {
                        const flyerUrl = await adminService.uploadEventFlyer(publicId, editSpecial.publicId, file)
                        setEditSpecial(prev => (prev ? { ...prev, flyerUrl } : null))
                        setSpecials(prev =>
                          prev.map(s => (s.publicId === editSpecial.publicId ? { ...s, flyerUrl } : s))
                        )
                      } catch (err) {
                        console.log('Failed to upload event flyer', err)
                      }
                      return
                    }

                    if (pendingFlyerPreviewUrl) {
                      URL.revokeObjectURL(pendingFlyerPreviewUrl)
                    }
                    setPendingFlyerFile(file)
                    setPendingFlyerPreviewUrl(URL.createObjectURL(file))
                  }}
                />
                {(editSpecial.flyerUrl || pendingFlyerFile) && (
                  <button
                    type="button"
                    className="styled-button cancel"
                    style={{ marginTop: '12px' }}
                    onClick={async () => {
                      if (!publicId) {
                        return
                      }

                      if (editSpecial.publicId && editSpecial.flyerUrl) {
                        try {
                          await adminService.deleteEventFlyer(publicId, editSpecial.publicId)
                          setEditSpecial(prev => (prev ? { ...prev, flyerUrl: null } : null))
                          setSpecials(prev =>
                            prev.map(s => (s.publicId === editSpecial.publicId ? { ...s, flyerUrl: null } : s))
                          )
                        } catch (err) {
                          console.log('Failed to delete event flyer', err)
                        }
                        return
                      }

                      if (pendingFlyerPreviewUrl) {
                        URL.revokeObjectURL(pendingFlyerPreviewUrl)
                      }
                      setPendingFlyerFile(null)
                      setPendingFlyerPreviewUrl(null)
                    }}
                  >
                    Remove flyer
                  </button>
                )}
              </div>
            </div>

            <div className="modal-footer-content">
              <button type="button" className="styled-button cancel" onClick={handleCancelEditSpecial}>
                Cancel
              </button>
              <button type="button" className="styled-button" onClick={handleSaveSpecial} disabled={isSavingSpecial}>
                {isSavingSpecial ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </Modal>
      <EventRsvpBlastModal
        isOpen={isRsvpBlastModalOpen}
        onClose={() => setIsRsvpBlastModalOpen(false)}
        events={specials}
        initialEventPublicId={eventRsvpsEventFilter || null}
      />
      {venueDetail?.publicId && (
        <BlastModal isOpen={isBlastModalOpen} onClose={() => setIsBlastModalOpen(false)} venuePublicId={venueDetail.publicId} />
      )}
      {venueDetail && (
        <BlastModal
          isOpen={!!editingBlast}
          onClose={() => setEditingBlast(null)}
          venuePublicId={venueDetail.publicId}
          editBlast={editingBlast}
          onSaved={saved => setBlasts(prev => prev.map(b => (b.publicId === saved.publicId ? toBlastRow(saved) : b)))}
        />
      )}
      <VenueBreadcrumbModal
        isOpen={!!selectedBreadcrumbPublicId}
        onClose={() => setSelectedBreadcrumbPublicId(null)}
        publicId={selectedBreadcrumbPublicId}
        onModerationChange={() => fetchVenueBreadcrumbs()}
        onDelete={async breadcrumbPublicId => {
          await adminService.deleteVenueBreadcrumb(breadcrumbPublicId)
        }}
        onDeleted={() => {
          setSelectedBreadcrumbPublicId(null)

          const isLastItemOnPage = breadcrumbs.length === 1
          if (isLastItemOnPage && breadcrumbsPage > 1) {
            setBreadcrumbsPage(breadcrumbsPage - 1)
            return
          }

          fetchVenueBreadcrumbs()
        }}
      />
      <StickerModal
        isOpen={!!selectedStickerPublicId}
        onClose={() => setSelectedStickerPublicId(null)}
        publicId={selectedStickerPublicId}
        onModerationChange={() => fetchVenueStickers()}
        onDelete={async stickerPublicId => {
          await adminService.deleteSticker(stickerPublicId)
        }}
        onDeleted={() => {
          setSelectedStickerPublicId(null)

          const isLastItemOnPage = stickers.length === 1
          if (isLastItemOnPage && stickersPage > 1) {
            setStickersPage(stickersPage - 1)
            return
          }

          fetchVenueStickers()
        }}
      />
      <VenueEventCommentModal
        isOpen={!!selectedCommentPublicId}
        onClose={() => setSelectedCommentPublicId(null)}
        publicId={selectedCommentPublicId}
        onChanged={() => fetchVenueEventComments()}
        onDeleted={() => {
          setSelectedCommentPublicId(null)

          const isLastItemOnPage = eventComments.length === 1
          if (isLastItemOnPage && eventCommentsPage > 1) {
            setEventCommentsPage(eventCommentsPage - 1)
            return
          }

          fetchVenueEventComments()
        }}
      />
    </div>
  )
}

export default VenueDetailPage
